import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import { useHasPermission } from '@/hooks/usePermissions';

export interface ClientPortalSettings {
  enabled: boolean;
}

/** The tenant's client-portal configuration (currently: enabled on/off). */
export function useClientPortalSettings() {
  // The GET is opened to company:view OR company:edit server-side; mirror that
  // so an edit-only admin can still load (and then toggle) the setting.
  const canView = useHasPermission('company:view');
  const canEdit = useHasPermission('company:edit');
  return useApiQuery<ClientPortalSettings>({
    queryKey: settingsKeys.clientPortal(),
    queryFn: () => apiGet<ClientPortalSettings>(operationsClient, settingsEndpoints.clientPortal()),
    enabled: canView || canEdit,
  });
}

export function useUpdateClientPortalSettings() {
  const queryClient = useQueryClient();
  return useApiMutation<ClientPortalSettings, Partial<ClientPortalSettings>>({
    mutationFn: (dto) =>
      apiPatch<ClientPortalSettings, Partial<ClientPortalSettings>>(
        operationsClient,
        settingsEndpoints.updateClientPortal(),
        dto,
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKeys.clientPortal(), data);
      toast.success(data.enabled ? 'Client portal enabled' : 'Client portal disabled');
    },
  });
}

/** Staff-initiated: email a contact their portal activation link. */
export function useInvitePortalContact() {
  return useApiMutation<{ invited: boolean }, { contactId: string }>({
    mutationFn: ({ contactId }) =>
      apiPost<{ invited: boolean }, undefined>(
        operationsClient,
        `/portal-admin/contacts/${encodeURIComponent(contactId)}/invite`,
        undefined,
      ),
  });
}
